"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";
import Chip from "@/components/global/ui/Chip";
import { cn } from "@/lib/utils";
import type { PageHomeSchema } from "@/sanity/pages/PageHomeTemplate";
import DemoMedia from "@/components/pages/ai-features/DemoMedia";
import { LiveDemo, MorningDemo, ReviewDemo } from "@/components/pages/ai-features/ProductDemos";
import AskStage from "./AskStage";
import CoachChat from "./CoachChat";
import styles from "./coach.module.scss";
import { COACH_CARDS, COACH_HEADER, type CoachMoment } from "./coachData";

/**
 * "A full trading day. Coached from open to close."
 * Figma: desktop 3165:31031, mobile 3205:60505, states 3083:28401.
 * Four cards with coded, animated product screens; on desktop a sticky chat
 * follows the active card and replays that moment as a conversation.
 */

const EASING = [0.22, 1, 0.36, 1] as const;
const VIEWPORT = { once: true, margin: "-50px" } as const;
const reveal = (y: number, delay = 0) => ({
  initial: { opacity: 0, y },
  whileInView: { opacity: 1, y: 0 },
  viewport: VIEWPORT,
  transition: { duration: 0.7, ease: [...EASING], delay },
});

const DEMO_LABELS: Record<CoachMoment, string> = {
  ask: "Ask TS Coach",
  morning: "Morning brief",
  live: "Live session",
  review: "Weekly review",
};

function FallbackTitle() {
  return (
    <h2>
      {COACH_HEADER.title[0]}
      <br />
      {COACH_HEADER.title[1]}
    </h2>
  );
}

export default function CoachSection({ data }: { data: PageHomeSchema }) {
  const { coachChip, coachTitle, coachText, coachCTA, coachAskCTA, heroCTA } = data;
  const trialCTA = coachCTA?.cta ? coachCTA : heroCTA;
  const trialText = coachCTA?.cta ? undefined : COACH_CARDS[1].cta;
  const askCTA = coachAskCTA?.cta ? coachAskCTA : heroCTA;
  const askText = coachAskCTA?.cta ? undefined : COACH_CARDS[0].cta;

  const cardsRef = useRef<HTMLDivElement>(null);
  // The chat only starts its first conversation once the cards scroll into view.
  const seen = useInView(cardsRef, { once: true, amount: 0.1 });
  const [active, setActive] = useState(0);
  const [ask, setAsk] = useState({ index: 0, revision: 0, at: 0 });
  const selectAsk = useCallback(
    (index: number) => setAsk((state) => ({ index, revision: state.revision + 1, at: Date.now() })),
    [],
  );

  // The card nearest the middle of the viewport drives the chat.
  useEffect(() => {
    const elements = cardsRef.current?.querySelectorAll<HTMLElement>("[data-coach-card]");
    if (!elements?.length) return;
    const entries = new Map<Element, IntersectionObserverEntry>();
    const observer = new IntersectionObserver(
      (changes) => {
        changes.forEach((entry) => entries.set(entry.target, entry));
        const visible = [...entries.values()]
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) setActive(Number((visible.target as HTMLElement).dataset.coachCard));
      },
      { rootMargin: "-15% 0px -35% 0px", threshold: [0, 0.15, 0.3, 0.5, 0.8] },
    );
    elements.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="ts-coach"
      className="relative z-[1] w-full overflow-x-clip bg-[#040404] px-[3rem] pt-[8rem] pb-[4rem] text-white lg:px-[30rem] lg:pt-[15rem] lg:pb-[10rem]">
      {/* Header */}
      <div className="flex w-full flex-col items-start gap-[2.5rem] lg:w-[120rem]">
        <motion.div {...reveal(24)}>
          <Chip
            text={coachChip || COACH_HEADER.chip}
            className="prose-p:text-white"
          />
        </motion.div>
        <motion.div
          {...reveal(32, 0.1)}
          className="richtext richtext-start w-full prose-h1:m-0 prose-h1:text-[4.5rem] prose-h1:leading-[1.04] prose-h1:font-normal prose-h1:tracking-[-0.03em] prose-h1:text-white lg:prose-h1:text-[9rem] prose-h2:m-0 prose-h2:text-[4.5rem] prose-h2:leading-[1.04] prose-h2:font-normal prose-h2:tracking-[-0.03em] prose-h2:text-white lg:prose-h2:text-[9rem]">
          {coachTitle?.length ? (
            <PortableText
              value={coachTitle}
              components={richTextComponent}
            />
          ) : (
            <FallbackTitle />
          )}
        </motion.div>
        <motion.p
          {...reveal(28, 0.2)}
          className="m-0 w-full text-[2rem] leading-[1.5] text-white/70 lg:w-[70rem] lg:text-[2.25rem]">
          {coachText || COACH_HEADER.text}
        </motion.p>
      </div>

      {/* Body */}
      <div className="mt-[4rem] lg:mt-[5rem] lg:grid lg:grid-cols-[127rem_50rem] lg:items-start lg:gap-[3rem]">
        <div
          ref={cardsRef}
          className="flex min-w-0 flex-col gap-[4rem] lg:gap-[3rem]">
          {COACH_CARDS.map((card, i) => {
            const isAsk = card.moment === "ask";
            const cta = isAsk ? askCTA : trialCTA;
            const ctaText = isAsk ? askText : trialText;
            return (
              <motion.article
                key={card.moment}
                data-coach-card={i}
                {...reveal(40, 0.05)}
                className="flex flex-col border border-white/[15%] bg-[#0a0a0a] p-[1rem]">
                <DemoMedia
                  label={DEMO_LABELS[card.moment]}
                  sizes="(max-width: 1023px) 92vw, 53vw"
                  className={styles.stage}>
                  {card.moment === "ask" ? (
                    <AskStage
                      selected={ask.index}
                      revision={ask.revision}
                      autoplay={active === 0}
                      onSelect={selectAsk}
                    />
                  ) : card.moment === "morning" ? (
                    <MorningDemo />
                  ) : card.moment === "live" ? (
                    <LiveDemo />
                  ) : (
                    <ReviewDemo />
                  )}
                </DemoMedia>
                <div className="flex flex-col px-[2rem] pt-[3.5rem] pb-[3rem] lg:flex-row lg:items-end lg:justify-between lg:gap-[3rem] lg:px-[3rem] lg:pt-[4rem]">
                  <div className="flex min-w-0 flex-col gap-[2rem] lg:max-w-[75rem] lg:flex-1 lg:gap-[1.5rem]">
                    <div className="flex flex-col gap-[1rem] lg:gap-[1.5rem]">
                      <p className="m-0 font-spacemono text-[1.75rem] leading-[1.4] text-white/60 uppercase lg:text-[2rem]">
                        {card.label}
                      </p>
                      <h3 className="m-0 text-[3rem] leading-[1.1] font-normal tracking-[-0.03em] text-white lg:text-[4rem]">
                        {card.title}
                      </h3>
                    </div>
                    <p className="m-0 text-[2rem] leading-[1.5] text-white/70 lg:text-[2.25rem]">{card.description}</p>
                  </div>
                  {cta && (
                    <div className="mt-[5rem] w-full shrink-0 lg:mt-0 lg:w-auto">
                      <CTAButton
                        data={cta}
                        overrideText={ctaText}
                        overrideVariant="default"
                        className="w-full sm:w-full lg:w-auto"
                      />
                    </div>
                  )}
                </div>
              </motion.article>
            );
          })}
        </div>

        {/* Sticky chat (desktop only; the mobile frame has no chat) */}
        <aside
          className={cn("sticky top-[max(12rem,calc(50vh-45rem))] hidden lg:block")}
          aria-label="TS Coach conversation preview">
          <motion.div {...reveal(24, 0.15)}>
            <CoachChat
              key={seen ? "live" : "idle"}
              moments={COACH_CARDS.map((card) => card.moment)}
              activeIndex={active}
              askIndex={ask.index}
              askedAt={ask.at}
              className="h-[90rem] max-h-[calc(100vh-14rem)] w-full"
            />
          </motion.div>
        </aside>
      </div>
    </section>
  );
}
